









import type { PropType } from '@vue/composition-api';
import {
  onBeforeUnmount,
  computed,
  defineComponent,
  onMounted,
  watch,
  ref,
} from '@vue/composition-api';
import { storeToRefs } from 'pinia';
import UniversalLoader from '@getaccept/dsr-shared/src/components/UniversalLoader.vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { useVisibilityStore } from '@getaccept/dsr-shared';
import { pusherClient } from '../api/pusher/pusher-client';
import { useSessionStore } from '../session/session.store';
import PublishedView from '../published/components/PublishedView.vue';
import { useParticipantsStore } from '../published/store/participants.store';

export default defineComponent({
  name: 'DsrPublished',
  components: {
    UniversalLoader,
    PublishedView,
  },
  props: {
    documentId: {
      type: String,
      default: '',
    },
    authId: {
      required: true,
      type: String,
    },
    recipientId: {
      required: true,
      type: String,
    },
    token: {
      required: true,
      type: Object as PropType<{
        jwt: string;
        exp: number;
      }>,
    },
    recipientFirstOpen: {
      required: true,
      type: Boolean,
    },
  },
  setup(props, { emit }) {
    const sessionStore = useSessionStore();
    const { documentId, token, tokenLoaded } = storeToRefs(sessionStore);
    const participantsStore = useParticipantsStore();
    const visibilityStore = useVisibilityStore();
    const loading = computed(() => !documentId.value || !tokenLoaded.value);

    watch(token, () => {
      if (props.token.jwt !== token.value.jwt) {
        emit('token-changed', token.value);
      }
    });

    watch(
      () => props.documentId,
      () => {
        sessionStore.setDocumentId(props.documentId);
      },
      { immediate: true }
    );

    watch(
      () => props.authId,
      () => {
        sessionStore.setAuthId(props.authId);
      },
      { immediate: true }
    );

    watch(
      () => props.recipientId,
      () => {
        sessionStore.setRecipientId(props.recipientId);
      },
      { immediate: true }
    );

    watch(
      () => props.recipientFirstOpen,
      () => {
        sessionStore.setRecipientFirstOpen(props.recipientFirstOpen);
      },
      { immediate: true }
    );

    watch(
      () => props.token,
      () => {
        if (tokenDiffers(props.token.jwt, token.value.jwt)) {
          sessionStore.setToken(props.token);
          connectPusher(props.token.jwt);
        }
      }
    );

    const tokenDiffers = (newJwt: string, oldJwt: string) => newJwt !== oldJwt;

    const isPusherConnected = ref(false);
    const connectPusher = (token: string) => {
      if (isPusherConnected.value) {
        pusherClient.updateToken(token);
      } else {
        isPusherConnected.value = true;
        pusherClient.connect(token);
      }
    };

    onMounted(() => {
      if (props.token.jwt && tokenDiffers(props.token.jwt, token.value.jwt)) {
        sessionStore.setToken(props.token);
        connectPusher(props.token.jwt);
      }
    });

    onBeforeUnmount(() => {
      sessionStore.reset();
      participantsStore.reset();
      visibilityStore.reset();
    });

    return { loading, t, connectPusher, isPusherConnected };
  },
});
